// Iter-158.13.2.20f.33b — reusable Character-level Fittings Notes.
//
// Extracted from CharacterDetail's inline block so the actual
// mounted H&M workflow (PrepMode's selected-character panel) can
// mount the EXACT same notes surface without duplicating logic.
// Human UAT of the initial .20f.33 build revealed the Fittings
// Notes had been added ONLY to `pages/CharacterDetail.jsx`,
// which is a different mounted route from where H&M users
// actually work (Fittings → Cast → selected-character panel
// inside `PrepMode`).
//
// Every write path is unchanged from .20f.33 — same endpoints,
// same tags (`["allergy"]`), same delete ACL (admin), same
// broadcast event so peer allergy-indicator surfaces refresh.
//
// Contract:
//   - `characterId` and `projectId` (required)
//   - `canManageNotes` — Contributor+ ACL for add/edit (from
//     parent's permission derivation, mirrors existing
//     `supervisor_notes` `contribute` enforce)
//   - `canAdminNotes` — Owner/Admin ACL for delete (mirrors
//     backend `admin` enforce on
//     `hmfull_delete_supervisor_note`)
//   - `currentUserId` — used to decide whether a Contributor
//     may edit their own note
//   - `onNotesLoaded(notes)` — optional callback so the parent
//     (CharacterDetail) can drive header AllergyBadge / hash
//     scroll from the same list without a duplicate fetch
//   - `allergyAnchorRef` — optional forwarded ref that gets
//     attached to the (at most one) allergy note row so the
//     parent can scrollIntoView programmatically
import { useCallback, useEffect, useState } from "react";
import { AlertOctagon, Pencil, Plus, Save, Trash2, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";
import { ALLERGY_NOTE_CHANGED_EVENT } from "../lib/allergy";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

export default function CharacterFittingsNotes({
    projectId,
    characterId,
    canManageNotes,
    canAdminNotes,
    currentUserId = null,
    onNotesLoaded,
    allergyAnchorRef,
    testid = "character-fittings-notes",
}) {
    const apiBase = useApiBase();
    const [notes, setNotes] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [addForm, setAddForm] = useState({ open: false, kind: null, text: "", editingId: null });

    // Same reader as .20f.33 CharacterDetail — no new endpoint.
    // Filter client-side to Character-level (no scene_id) notes
    // belonging to this character.
    const refresh = useCallback(async () => {
        if (!apiBase || !projectId || !characterId) return;
        try {
            const { data } = await api.get(`${apiBase}/${projectId}/supervisor-notes`);
            const all = Array.isArray(data) ? data : [];
            const mine = all.filter(
                (n) =>
                    n
                    && n.character_id === characterId
                    && !n.scene_id
                    && n.archived !== true,
            );
            setNotes(mine);
            if (typeof onNotesLoaded === "function") onNotesLoaded(mine);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoaded(true);
        }
    }, [apiBase, projectId, characterId, onNotesLoaded]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Peer surfaces (SceneDetail, BetaHMCharacters, PrepMode itself
    // when mounted alongside another instance) may write allergy
    // notes; keep this list in sync.
    useEffect(() => {
        const handler = () => refresh();
        window.addEventListener(ALLERGY_NOTE_CHANGED_EVENT, handler);
        return () => window.removeEventListener(ALLERGY_NOTE_CHANGED_EVENT, handler);
    }, [refresh]);

    const allergyNote = notes.find((n) => (n.tags || []).includes("allergy")) || null;

    if (!canManageNotes) return null;

    const openAddNote = () =>
        setAddForm({ open: true, kind: "note", text: "", editingId: null });
    const openAddAllergy = () =>
        setAddForm({ open: true, kind: "allergy", text: "", editingId: null });
    const openEditAllergy = () =>
        allergyNote
            ? setAddForm({
                  open: true,
                  kind: "allergy",
                  text: allergyNote.text || "",
                  editingId: allergyNote.id,
              })
            : openAddAllergy();
    const openEditNote = (note) =>
        setAddForm({
            open: true,
            kind: (note.tags || []).includes("allergy") ? "allergy" : "note",
            text: note.text || "",
            editingId: note.id,
        });
    const cancel = () =>
        setAddForm({ open: false, kind: null, text: "", editingId: null });

    const save = async () => {
        const trimmed = (addForm.text || "").trim();
        if (!trimmed) {
            setError("Note text cannot be empty.");
            return;
        }
        setBusy(true);
        setError("");
        const isAllergy = addForm.kind === "allergy";
        const tags = isAllergy ? ["allergy"] : [];
        try {
            const payload = {
                text: trimmed,
                tags,
                scene_id: null,
                character_id: characterId,
                visibility: "team",
            };
            if (addForm.editingId) {
                await api.patch(
                    `${apiBase}/${projectId}/supervisor-notes/${addForm.editingId}`,
                    payload,
                );
            } else {
                await api.post(`${apiBase}/${projectId}/supervisor-notes`, payload);
            }
            cancel();
            await refresh();
            if (isAllergy) {
                window.dispatchEvent(new Event(ALLERGY_NOTE_CHANGED_EVENT));
            }
            toast.success(
                addForm.editingId
                    ? isAllergy ? "Allergy note updated" : "Note updated"
                    : isAllergy ? "Allergy note added" : "Note added",
            );
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (note) => {
        if (!note || !note.id) return;
        // eslint-disable-next-line no-alert
        const ok = window.confirm(
            (note.tags || []).includes("allergy")
                ? "Remove this allergy note? The safety indicator will disappear from every Scene."
                : "Remove this note?",
        );
        if (!ok) return;
        setBusy(true);
        setError("");
        try {
            await api.delete(
                `${apiBase}/${projectId}/supervisor-notes/${note.id}`,
            );
            await refresh();
            if ((note.tags || []).includes("allergy")) {
                window.dispatchEvent(new Event(ALLERGY_NOTE_CHANGED_EVENT));
            }
            toast.success("Note removed");
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <section
            data-testid={`${testid}-section`}
            className="mt-6 rounded-xl border border-white/10 bg-black/20 overflow-hidden"
        >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5">
                <div
                    data-testid={`${testid}-heading`}
                    className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 font-semibold flex-1"
                >
                    Fittings Notes
                </div>
                {allergyNote ? (
                    <button
                        type="button"
                        data-testid={`${testid}-allergy-edit`}
                        onClick={openEditAllergy}
                        className="tap inline-flex items-center gap-1 h-7 px-2 rounded-md border border-red-400/40 bg-red-500/10 text-red-200 text-[11px] font-semibold hover:bg-red-500/20"
                    >
                        <AlertOctagon size={11} /> Edit allergy
                    </button>
                ) : (
                    <button
                        type="button"
                        data-testid={`${testid}-allergy-add`}
                        onClick={openAddAllergy}
                        className="tap inline-flex items-center gap-1 h-7 px-2 rounded-md border border-red-400/40 bg-red-500/10 text-red-200 text-[11px] font-semibold hover:bg-red-500/20"
                    >
                        <AlertOctagon size={11} /> Add allergy
                    </button>
                )}
                <button
                    type="button"
                    data-testid={`${testid}-add`}
                    onClick={openAddNote}
                    className="tap inline-flex items-center gap-1 h-7 px-2 rounded-md border border-white/15 text-neutral-200 text-[11px] hover:bg-white/5"
                >
                    <Plus size={11} /> Note
                </button>
            </div>
            {addForm.open ? (
                <div
                    data-testid={`${testid}-form`}
                    className="px-4 py-3 border-b border-white/5 bg-white/[0.02]"
                >
                    <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1.5">
                        {addForm.kind === "allergy"
                            ? addForm.editingId ? "Edit allergy" : "Allergy · free text"
                            : addForm.editingId ? "Edit note" : "New note"}
                    </div>
                    <Textarea
                        data-testid={`${testid}-form-text`}
                        value={addForm.text}
                        onChange={(e) => setAddForm((f) => ({ ...f, text: e.target.value }))}
                        placeholder={
                            addForm.kind === "allergy"
                                ? "e.g. Latex + adhesive sensitivity. Use hypoallergenic mastic only."
                                : "Fitting details for this character…"
                        }
                        rows={3}
                        className="text-sm"
                    />
                    {error ? (
                        <div
                            data-testid={`${testid}-form-error`}
                            className="mt-1.5 text-[11px] text-red-300"
                        >
                            {error}
                        </div>
                    ) : null}
                    <div className="mt-2 flex items-center justify-end gap-2">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            data-testid={`${testid}-form-cancel`}
                            onClick={cancel}
                            disabled={busy}
                        >
                            <XIcon size={12} /> Cancel
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            data-testid={`${testid}-form-save`}
                            onClick={save}
                            disabled={busy}
                        >
                            <Save size={12} /> Save
                        </Button>
                    </div>
                </div>
            ) : null}
            {loaded && notes.length === 0 && !addForm.open ? (
                <div
                    data-testid={`${testid}-empty`}
                    className="px-4 py-6 text-center text-[11px] text-neutral-500"
                >
                    No fittings notes yet.
                </div>
            ) : null}
            {notes.length > 0 ? (
                <ul
                    data-testid={`${testid}-list`}
                    className="divide-y divide-white/5"
                >
                    {/* Iter-158.13.2.20f.33d — Allergy rows are pinned
                        to the TOP of the list regardless of the
                        server-side `created_at` sort so the safety
                        record is the FIRST thing a crew member sees.
                        Sort is stable-partition: allergy first (in
                        original relative order), then ordinary notes
                        (in original relative order). Predicate stays
                        strictly tag-based — an ordinary note whose
                        text happens to contain "latex" is NOT
                        promoted; only notes explicitly tagged
                        "allergy" trigger the safety visual. */}
                    {[...notes]
                        .sort((a, b) => {
                            const aA = (a?.tags || []).includes("allergy") ? 0 : 1;
                            const bA = (b?.tags || []).includes("allergy") ? 0 : 1;
                            return aA - bA;
                        })
                        .map((n) => {
                        const isAllergy = (n.tags || []).includes("allergy");
                        const isCreator = currentUserId && n.created_by === currentUserId;
                        const canEdit = canAdminNotes || (canManageNotes && isCreator);
                        const canRemove = canAdminNotes;
                        // Iter-158.13.2.20f.33d — unmistakable safety
                        // visual for allergy rows. Full-width red
                        // left-border + faint red background tint so
                        // no crew member can confuse an ordinary
                        // Fittings note with a registered allergy.
                        // Ordinary notes remain plain.
                        const rowClass = isAllergy
                            ? "px-4 py-3 pl-3 flex items-start gap-3 border-l-4 border-red-500/70 bg-red-500/[0.08]"
                            : "px-4 py-3 flex items-start gap-3";
                        return (
                            <li
                                key={n.id}
                                data-testid={`${testid}-item-${n.id}`}
                                data-allergy={isAllergy ? "true" : "false"}
                                ref={isAllergy && allergyAnchorRef ? allergyAnchorRef : undefined}
                                id={isAllergy ? "allergy" : undefined}
                                className={rowClass}
                            >
                                <div className="min-w-0 flex-1">
                                    {isAllergy ? (
                                        <div
                                            data-testid={`${testid}-item-label-${n.id}`}
                                            className="mb-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-red-400/50 bg-red-500/20 text-red-100 text-[10px] uppercase tracking-[0.2em] font-bold"
                                        >
                                            <AlertOctagon size={11} /> Allergy
                                        </div>
                                    ) : null}
                                    <div
                                        data-testid={`${testid}-item-text-${n.id}`}
                                        className={
                                            isAllergy
                                                ? "text-sm text-red-50 whitespace-pre-wrap break-words font-medium"
                                                : "text-sm text-neutral-100 whitespace-pre-wrap break-words"
                                        }
                                    >
                                        {n.text}
                                    </div>
                                    {n.created_by_name ? (
                                        <div className="text-[10px] text-neutral-500 mt-1">
                                            {n.created_by_name}
                                        </div>
                                    ) : null}
                                </div>
                                {canEdit ? (
                                    <button
                                        type="button"
                                        data-testid={`${testid}-edit-${n.id}`}
                                        onClick={() => openEditNote(n)}
                                        aria-label="Edit note"
                                        title="Edit"
                                        className="tap inline-flex items-center justify-center h-7 w-7 rounded-md text-neutral-300 hover:bg-white/5"
                                    >
                                        <Pencil size={12} />
                                    </button>
                                ) : null}
                                {canRemove ? (
                                    <button
                                        type="button"
                                        data-testid={`${testid}-remove-${n.id}`}
                                        onClick={() => remove(n)}
                                        aria-label="Remove note"
                                        title="Remove (admin only)"
                                        className="tap inline-flex items-center justify-center h-7 w-7 rounded-md text-red-300 hover:bg-red-500/10"
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                ) : null}
                            </li>
                        );
                    })}
                </ul>
            ) : null}
        </section>
    );
}
